import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "@tamagui/lucide-icons";
import { BlurView } from "expo-blur";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateUserProfile } from "@/api/mutations/";
import type { UserProfileFormData } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/";
import {
  Button,
  Footer,
  H3,
  Header,
  Heading,
  ScrollView,
  Section,
  Separator,
  XStack,
  YStack,
  getTokenValue,
  useMedia,
} from "@/components";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import {
  carrierMemberIdRefinement,
  carrierSchema,
} from "@/forms/carrierSchema";
import { optionalMbiZodString } from "@/forms/mbiSchema";
import { InsuranceInformation } from "../profile/InsuranceInformation";

/** The server names locked fields as it stores them; nested ones are dotted. */
const MBI_FIELD = "insurance.medicare_beneficiary_id";

export const insuranceSchema = z
  .object({
    carrier: carrierSchema.refine(
      (value) => !value.carrierName || !!value.bridgePayerId,
      { message: "Please select your insurance carrier from the list" },
    ),
    planName: z.string().optional(),
    memberId: z.string(),
    medicareBeneficiaryId: optionalMbiZodString,
    groupNumber: z.string(),
    rxBin: z.string().optional(),
    rxPcn: z.string().optional(),
    rxGroup: z.string().optional(),
    userIsPolicyHolder: z.boolean(),
  })
  .superRefine(carrierMemberIdRefinement);

export type InsuranceSchemaType = z.infer<typeof insuranceSchema>;

const toFormValues = (
  data: UserProfileFormData | null,
): InsuranceSchemaType => ({
  carrier: {
    carrierName: data?.carrierName ?? "",
    bridgePayerId: data?.bridgePayerId ?? "",
    requiresMemberId: true,
  },
  planName: data?.planName ?? "",
  memberId: data?.memberId ?? "",
  medicareBeneficiaryId: data?.medicareBeneficiaryId ?? "",
  groupNumber: data?.groupNumber ?? "",
  rxBin: data?.rxBin ?? "",
  rxPcn: data?.rxPcn ?? "",
  rxGroup: data?.rxGroup ?? "",
  userIsPolicyHolder: data?.userIsPolicyHolder ?? false,
});

export default function InsuranceSettingsScreen() {
  const { lg } = useMedia();
  const { data } = useGetUserProfile();
  const serverFormData = data as UserProfileFormData | null;
  const isLocked = Boolean(data?.lockedFields?.includes(MBI_FIELD));

  const { mutate: updateUserProfile, isPending: isUpdating } =
    useUpdateUserProfile();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<InsuranceSchemaType>({
    resolver: zodResolver(insuranceSchema),
    mode: "onBlur",
    defaultValues: toFormValues(serverFormData),
  });

  useEffect(() => {
    if (serverFormData) {
      reset(toFormValues(serverFormData));
    }
  }, [serverFormData, reset]);

  const handleSave = handleSubmit((formData) => {
    const { carrier, ...rest } = formData;
    updateUserProfile(
      {
        data: {
          ...rest,
          carrierName: carrier.carrierName,
          bridgePayerId: carrier.bridgePayerId,
        },
      },
      {
        onSuccess: () => {
          reset(formData);
        },
      },
    );
  });

  const handleDiscard = () => {
    if (serverFormData) {
      reset(toFormValues(serverFormData));
    }
  };

  return (
    <Section flex={1} $lg={{ flex: 1, gap: "$250" }}>
      {lg && (
        <Header
          marginHorizontal="$screenPadding"
          marginTop="$screenPadding"
          gap="$250"
        >
          <H3>Insurance</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      <ScrollView
        contentContainerStyle={{
          margin: "$screenPadding",
          marginBottom: "$1000",
          ...(lg && {
            marginTop: "$50",
          }),
        }}
      >
        <YStack gap="$200">
          <Heading size="$sm">Insurance Information</Heading>
          <InsuranceInformation
            control={control}
            errors={errors}
            isLocked={isLocked}
          />
        </YStack>
      </ScrollView>
      {isDirty && !isLocked && (
        <BlurView
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
          }}
        >
          <Footer
            flexDirection="row"
            padding="$screenPadding"
            marginLeft="auto"
            gap="$50"
            alignSelf="flex-end"
          >
            <Button mode="tertiary" onPress={handleDiscard}>
              Discard
            </Button>
            <Button
              mode="primary"
              loading={isUpdating}
              icon={!isUpdating && <Check />}
              onPress={handleSave}
              disabled={isUpdating || !isDirty}
            >
              {isUpdating && (
                <XStack marginRight="$150">
                  <LoadingSpinner size={getTokenValue("$300")} />
                </XStack>
              )}
              Save changes
            </Button>
          </Footer>
        </BlurView>
      )}
    </Section>
  );
}
